import React, { useEffect, useState, useRef } from 'react';
import { View, StyleSheet, Platform, useWindowDimensions, KeyboardAvoidingView, ScrollView, Keyboard, type ScrollViewInstance } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Portal, Modal, useTheme } from '@/components/ui';
import { computeFlexModalTopInset } from '@/utils/modal-insets';

interface FlexModalProps {
	visible: boolean;
	onDismiss: () => void;
	children: React.ReactNode;
	fullScreenBreakpoint?: number;
}

export const FlexModal: React.FC<FlexModalProps> = ({
	visible,
	onDismiss,
	children,
	fullScreenBreakpoint = 600,
}) => {
	const theme = useTheme();
	const { width, height } = useWindowDimensions();
	const insets = useSafeAreaInsets();
	const isMobile = Platform.OS !== 'web';
	const isSmallScreen = width < fullScreenBreakpoint;
	// Use full-screen only on small mobile screens (phones), not tablets/iPads
	// For tablets/iPads, use centered 90% modal
	const useFullScreen = isMobile && isSmallScreen;

	// Track keyboard visibility and height
	const [keyboardHeight, setKeyboardHeight] = useState(0);
	const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
	const scrollViewRef = useRef<ScrollViewInstance>(null);

	useEffect(() => {
		if (!isMobile || !visible) return;

		const keyboardWillShowListener = Keyboard.addListener(
			Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow',
			(event) => {
				setKeyboardHeight(event.endCoordinates.height);
				setIsKeyboardVisible(true);
				// Small delay to ensure input is focused before scrolling
				setTimeout(() => {
					scrollViewRef.current?.scrollToEnd({ animated: true });
				}, 100);
			},
		);

		const keyboardWillHideListener = Keyboard.addListener(
			Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide',
			() => {
				setKeyboardHeight(0);
				setIsKeyboardVisible(false);
			},
		);

		return () => {
			keyboardWillShowListener.remove();
			keyboardWillHideListener.remove();
		};
	}, [isMobile, visible]);

	return (
		<Portal>
			<Modal
				visible={visible}
				onDismiss={onDismiss}
				contentContainerStyle={[
					styles.container,
					{
						backgroundColor: theme.colors.background,
						// When keyboard is visible on mobile, make modal full-screen above keyboard
						...(isKeyboardVisible && isMobile
							? {
								position: 'absolute',
								// Issue #128: use real safe-area insets instead of the
								// old magic `25` so the close-X clears the notch /
								// Dynamic Island on modern iPhones.
								top: insets.top,
								left: 0,
								right: 0,
								margin: 0,
								padding: 16,
								paddingTop: computeFlexModalTopInset(insets, {
									isFullScreen: true,
									basePadding: 16,
								}),
								width: '100%',
								maxWidth: '100%',
								height: height - keyboardHeight - insets.top,
								maxHeight: height - keyboardHeight - insets.top,
								minHeight: height - keyboardHeight - insets.top,
								borderRadius: 0,
								alignSelf: 'stretch',
								justifyContent: 'flex-start',
							}
							: useFullScreen
								? {
									...styles.fullScreenContainer,
									// Issue #128: push content below the notch on iPhone.
									paddingTop: computeFlexModalTopInset(insets, {
										isFullScreen: true,
										basePadding: 16,
									}),
									maxHeight: '100%',
								}
								: {
									...styles.centeredContainer,
									minHeight: Math.min(height * 0.6, 600),
									maxHeight: '90%',
								}),
					},
				]}
			>
				<KeyboardAvoidingView
					behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
					style={{ flex: 1 }}
					keyboardVerticalOffset={isKeyboardVisible && isMobile
						? (Platform.OS === 'ios' ? 0 : 0)
						: (Platform.OS === 'ios' ? 0 : 20)
					}
					enabled={isMobile}
				>
					<ScrollView
						ref={scrollViewRef}
						style={{ flex: 1 }}
						contentContainerStyle={[
							{
								flexGrow: 1,
								paddingBottom: isKeyboardVisible && isMobile ? 20 : 20,
							},
							useFullScreen || (isKeyboardVisible && isMobile)
								? { minHeight: isKeyboardVisible ? height - keyboardHeight - 32 : height - 32 }
								: { minHeight: Math.min(height * 0.5, 600) - 40 },
						]}
						keyboardShouldPersistTaps="handled"
						showsVerticalScrollIndicator={true}
					>
						<View style={[styles.content, (useFullScreen || (isKeyboardVisible && isMobile)) && styles.fullScreenContent]}>
							{children}
						</View>
					</ScrollView>
				</KeyboardAvoidingView>
			</Modal>
		</Portal>
	);
};

const styles = StyleSheet.create({
	container: {
		borderRadius: 10,
	},
	content: {
		width: '100%',
	},
	fullScreenContent: {
		flex: 1,
		minHeight: '100%',
	},
	fullScreenContainer: {
		margin: 0,
		padding: 16,
		width: '100%',
		maxWidth: '100%',
		maxHeight: '100%',
		minHeight: '100%',
		height: '100%',
		borderRadius: 0,
		alignSelf: 'stretch',
		justifyContent: 'flex-start',
	},
	centeredContainer: {
		margin: 20,
		padding: 20,
		width: '90%',
		maxWidth: '90%',
		maxHeight: '90%',
		alignSelf: 'center',
	},
});
